import { Suspense, lazy } from 'react';

import { KpiGrid, Nota, SecaoTitulo } from '../../shared/ui/primitivas';

/**
 * Recharts entra por `lazy` porque é a única tela que o usa e ele custa
 * 111 KB gzip — no pacote principal, a fila de triagem e a captação
 * pagariam por um gráfico que nunca abrem.
 */
const BarrasDeRanking = lazy(() =>
  import('../../shared/ui/graficos').then((m) => ({ default: m.BarrasDeRanking })),
);

/** Reserva a altura do gráfico: sem isto a página pula quando ele chega. */
function Esqueleto({ altura }: { altura: number }) {
  return <div style={{ height: altura }} aria-hidden="true" />;
}
import { TelaAnery } from '../../shared/ui/TelaAnery';
import type { AneryChip, AneryKpi } from '../../shared/ui/tiposDaEsteira';

/**
 * Número do mês, e só. A fila de trabalho do dia é a Visão geral — quem abre
 * isto quer saber como foi o mês, não o que fazer agora.
 *
 * Os indicadores NÃO reusam `explainability_score` / `compliance_score` /
 * `bias_score` do painel de governança: aqueles são default de DDL sem
 * writer — número sem medição atrás. Os daqui declaram origem, um a um.
 *
 * Fronteira da fonte: vira `useQuery` aqui quando houver o que medir.
 */
const DASHBOARD = {
  breadcrumb: 'apoio / dashboards / diretoria · setembro-2026',
  titulo: 'Dashboard executivo',
  subtitulo:
    'Cada número com origem declarada, bebendo da mesma fonte que a operação — para parar de divergir da planilha.',
  chips: [
    { label: '· 28,4% da carteira', strong: 'Bradesco', accent: true },
    { label: 'janelas internas ', strong: 'dia 3 · 10 · 23' },
    { label: 'humano em toda saída externa' },
  ] as AneryChip[],
  kpis: [
    {
      label: 'Erro barrado antes do envio',
      value: 'R$ 96,4 mil',
      detail: 'não virou glosa',
      source: 'origem: validadores',
    },
    {
      label: 'Pacientes Bradesco ativos',
      value: '312',
      detail: '28,4% da carteira',
      source: 'origem: IW',
    },
    {
      label: 'Dossiês prontos antes da janela',
      value: '88%',
      delta: '↑ 31 p.p. vs baseline',
      source: 'origem: camada de trabalho',
    },
    {
      label: 'Fechamento de prestadores',
      value: '1,8 dia',
      detail: 'era 6+ em planilha',
      source: 'origem: comparador',
    },
  ] as AneryKpi[],
  /**
   * Pendências abertas por operadora, no ciclo. Barra horizontal porque o
   * rótulo é nome de operadora — na vertical ele inclina ou trunca, e o
   * nome é metade da informação.
   *
   * As oito maiores e "Outras". Uma nona cor não existe: a nona operadora
   * entra no agregado, que é o que a regra manda fazer com a cauda.
   */
  pendencias: [
    { nome: 'Bradesco', valor: 54 },
    { nome: 'Pasa', valor: 22 },
    { nome: 'Outras 15', valor: 21 },
    { nome: 'Amil', valor: 19 },
    { nome: 'Santos', valor: 15 },
    { nome: 'Guarulhos', valor: 12 },
    { nome: 'Unimed Seg.', valor: 9 },
    { nome: 'SulAmérica', valor: 7 },
  ],
  /**
   * Sem barra no SLA, e não é o motivo do funil: aqui o denominador existe,
   * mas os quatro valores vivem entre 88% e 97% — num trilho de 0 a 100,
   * nove décimos do traço são iguais nas quatro linhas e o olho não separa
   * 88 de 93. Quatro números em mono se comparam de imediato.
   */
  sla: [
    { nome: 'Captação → aceite', valor: '97%' },
    { nome: 'Dossiê → janela', valor: '88%' },
    { nome: 'Fechamento → XML', valor: '93%' },
    { nome: 'Recurso no prazo', valor: '91%' },
  ],
  /** Percentual do total de glosa, não valor. O sufixo diz isso na barra. */
  causaRaiz: [
    { label: 'Linear', valor: 41, detalhe: 'reversão ≈95%' },
    { label: 'Fracionamento 1704', valor: 23, detalhe: 'centavos na guia' },
    { label: 'Documental', valor: 19, detalhe: 'vira regra no checklist' },
    { label: 'Técnica', valor: 17, detalhe: 'exige evidência clínica' },
  ],
  nota: 'Os números acima só valem contra a medição de horas de backoffice por paciente feita antes de automatizar — é a história mais importante da Fase 0.',
};

export function PaginaDoDashboard(): React.JSX.Element {
  return (
    <TelaAnery
      breadcrumb={DASHBOARD.breadcrumb}
      titulo={DASHBOARD.titulo}
      subtitulo={DASHBOARD.subtitulo}
      chips={DASHBOARD.chips}
    >
      <SecaoTitulo extra="com origem declarada">O mês</SecaoTitulo>
      <KpiGrid kpis={DASHBOARD.kpis} />

      <SecaoTitulo extra="origem: tickets da plataforma">
        Pendências por operadora · ciclo atual
      </SecaoTitulo>
      <div className="mt-3">
        <Suspense fallback={<Esqueleto altura={250} />}>
          <BarrasDeRanking dados={DASHBOARD.pendencias} altura={250} />
        </Suspense>
      </div>

      <SecaoTitulo extra="onde a glosa nasce">Glosa · causa-raiz</SecaoTitulo>
      <div className="mt-3">
        <Suspense fallback={<Esqueleto altura={150} />}>
          <BarrasDeRanking
            dados={DASHBOARD.causaRaiz.map((c) => ({ nome: c.label, valor: c.valor }))}
            sufixo="%"
            altura={150}
          />
        </Suspense>
      </div>

      <SecaoTitulo>SLA por etapa do ciclo</SecaoTitulo>
      <div>
        {DASHBOARD.sla.map((e) => (
          <div key={e.nome} className="anery-item">
            <span className="num w-[4rem] shrink-0 text-[0.9375rem] font-medium text-[var(--navy)]">
              {e.valor}
            </span>
            <span className="min-w-0 flex-1 text-[0.875rem]">{e.nome}</span>
          </div>
        ))}
      </div>

      <Nota titulo="Sem baseline não existe gate.">{DASHBOARD.nota}</Nota>
    </TelaAnery>
  );
}
